import React from 'react';
import { useMsal } from '@azure/msal-react';
import Header from './Header';
import HeroBanner from './HeroBanner';
import ImageGallery from './ImageGallery';
import { LOGIN_SCOPES } from '../auth/msalConfig';

export default function HomePage() {
  const { instance } = useMsal();

  const handleEmployeeLogin = () => {
    sessionStorage.setItem('postLoginRedirect', '/microsite');
    // Includes the API scope so acquireTokenSilent can mint access tokens later.
    instance.loginRedirect({
      scopes: LOGIN_SCOPES
    });
  };

  return (
    <div>
      <Header />
      <HeroBanner 
              backgroundImage="/images/2027/CircleofExcellenceHeader.jpg"
      />
      
      <nav className="nav">
        <div className="wrapper">
          <ul>
            <li><a href="#overview">OVERVIEW</a></li>
            <li><a href="#destination">DESTINATION</a></li>
            <li><a href="#inspiration">INSPIRATION GALLERY</a></li>
            <li><a href="#emp-login">EMPLOYEE LOGIN</a></li>
          </ul>
        </div>
      </nav>
      
      <section id="intro" className="intro">
        <div className="wrapper">
          <p>
           Circle of Excellence winners and their guests will journey to Anguilla. Tucked away in the northern Caribbean, Anguilla is a rare blend of natural beauty, understated luxury, and authentic island charm. Known for its pristine white sand beaches and crystal clear turquoise waters, the island offers a tranquil escape where time slows and every moment feels intentional.
          </p>
          <p>
           Unspoiled, elegant, and endlessly inviting, Anguilla isn’t just a place you visit - it’s a place you feel. Once discovered, it’s never forgotten.
          </p>
        </div>
      </section>

      <section id="overview" className="overview">
        <div className="wrapper">
          <p>
           Each Circle of Excellence winner and his or her guest* will be awarded round-trip airline tickets from their home city, 5 days and 4 nights at the beautiful Aurora Anguilla Resort & Golf Club, a luxury AAA-4 Diamond resort, inclusive of exquisite cuisine and incredible adventures.
          </p>
          <p>
           Circle of Excellence signifies exceptional accomplishment. The highly competitive program offers attendance to a select^ number of employees per year.
          </p>
          <p>
           *Award winners are allowed to bring one adult guest. No additional guests and/or children permitted. Please review the Circle of Excellence Qualifications and Rules for more detailed information.
          </p>
          <p>
           ^Selection will be based on several factors, such as overall revenue from FY26 sales, sales quota achievement percentages, and specific deal categories. Additional factors, such as performance in other areas or categories, will also be considered. Inclusion may be awarded in lieu of prizes for other sales-incentive contests. Any questions or interpretations relating to this special recognition program and any prizes and incentives awarded will be subject to final approval of the company's SVP, GTM Operations. The company reserves the right to modify or cancel this recognition program at any time.
          </p>
        </div>
      </section>

      <section id="destination" className="destination">
        <div className="wrapper">
          <ImageGallery images={[
            '/images/2027/04ATBContent97.jpg',
            '/images/2027/01-GoatCave.jpg',
            '/images/2027/14-AuroraAnguillaRendezvousBay.jpg',
            '/images/2027/03-AuroraAnguilla13.jpg',
            '/images/2027/07-Arch.jpg',
            '/images/2027/17-Spa.jpg',
            '/images/2027/16-BeachFrontSuiteBedroom.jpg'
          ]} />
          
          <p>
           Discover paradise in Anguilla — where powder-soft white sand beaches, shimmering tropical seas, and effortless Caribbean luxury create the ultimate island escape. Indulge in world-class dining, breathtaking ocean views, and unforgettable adventures before retreating to the elegance of Aurora Anguilla Resort & Golf Club, a premier beachfront sanctuary featuring championship golf, lavish accommodations, rejuvenating spa experiences, and unmatched island hospitality. Your dream getaway begins here.
          </p>
        </div>
      </section>

      <section id="inspiration" className="inspiration">
        <div className="wrapper">
          <ImageGallery images={[
            '/images/2027/05-AuroraAnguillaGolfcourse2.jpg',
            '/images/2027/06-AuroraWaterParkDroneShots.jpg',
            '/images/2027/13-Kayak.jpg',
            '/images/2027/12-AuroraSoranaSpa.jpg',
            '/images/2027/09-AuroraAnguillaBeach1.jpg',
            '/images/2027/08-Content.jpg',
            '/images/2027/15-MarywetherPool.jpg'
          ]} />
        </div>
      </section>

      <section className="attractions">
        <div className="wrapper">
          <div className="cards-container">
            <div className="card">
              <img src="/images/fg/highlight-img1.png" alt="proximity image" />
              <h3>REFINED COMFORT</h3>
              <p>
               Aurora Anguilla Resort & Golf Club is where Caribbean beauty meets effortless luxury. 
              </p>
              <p>
               Guests are immersed in a world of elevated comfort. With expansive ocean-view accommodations, guests will wake up steps from the sea, where turquoise waters ignite every day with possibility. Food lovers will delight in an exceptional collection of dining experiences. For those seeking adventure or restoration, Aurora offers it all: tranquil spa treatments, water sports in crystal clear seas, and the island’s only championship 18 hole Greg Norman designed golf course, where lush fairways meet dramatic coastal views.
              </p>
            </div>
            <div className="card">
              <img src="/images/Icons/COE_Maui_Icon_SpectacularBeauty.png" alt="beauty image" />
              <h3>EXPLORE</h3>
              <p>
               Anguilla is a playground of sun, sea, and unforgettable experiences. Start your days on powder soft beaches—from iconic Shoal Bay to hidden coves waiting to be discovered—where turquoise waters invite swimming, snorkeling, and sailing straight from the shore. Offshore, vibrant reefs and shipwrecks offer world class diving, while calm seas make island hopping and sunset cruises effortless.
              </p>
              <p>
               Beyond the beach, Anguilla surprises at every turn. Savor the island’s legendary culinary scene, where beachfront grills and upscale restaurants serve up fresh seafood infused with global flair. Explore historic villages and heritage sites that tell stories of resilience and rhythm, then follow the sound of live music to a beach bar where the island’s soul comes alive after dark.
              </p>              
            </div>
            <div className="card">
              <img src="/images/Icons/COE_Maui_Icon_Tranquility.png" alt="indulgence image" />
              <h3>HAVE FUN</h3>
              <p>
               For those seeking active luxury, Anguilla delivers championship golf, bespoke wellness experiences, paddleboarding at dawn, and evenings soundtracked by live music beneath the stars. Here, every experience feels exclusive, unhurried, and beautifully tailored—allowing visitors to design days filled with meaning, beauty, and quiet distinction.
              </p>
              <p>
               Anguilla is not about doing more—it’s about experiencing better.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="emp-login" className="emp-login">
        <div className="wrapper" style={{
                  backgroundImage: 'url(/images/2027/09-AuroraAnguillaBeach1.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}>
          <h2>
           Take this chance to immerse yourself in Anguilla’s charm, build unforgettable moments, and experience the Caribbean at its finest.
          </h2>
          <div className="half-block">
            <button onClick={handleEmployeeLogin} className="cta">
              <span>Employee Login</span> →
            </button>
            <div className="desc">
              Sign in with your Infor network credentials to view the COE leaderboard and program qualifications.
            </div>
          </div>
          <div className="half-block">
            <a href="mailto:circleofexcellence@infor.com" className="cta">
              <span>Contact Us</span> →
            </a>
            <div className="desc">
              Send us an email at <a href="mailto:circleofexcellence@infor.com">circleofexcellence@infor.com</a>
            </div>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="wrapper">
          <div className="legal">&copy; {new Date().getFullYear()} Infor. All rights reserved.</div>
          <div className="brand">Designed and built by Infor Marketing</div>
        </div>
      </footer>
    </div>
  );
}